package com.siriusxm.pia.views.unifiedaggregator.dataflow

import androidx.compose.runtime.*
import kotlinx.browser.document
import org.w3c.dom.Element

/**
 * Represents a point in 2D space for drawing connections.
 */
data class Point(val x: Double, val y: Double)

/**
 * Represents a connection between two nodes that should be drawn.
 */
data class Connection(
    val fromId: String,
    val toId: String,
    val highlighted: Boolean
)

/**
 * Generates a cubic bezier path string for an SVG <path> element.
 * Creates a smooth curved line between two points.
 *
 * @param start The starting point of the curve
 * @param end The ending point of the curve
 * @return SVG path data string (e.g., "M 0 0 C 50 0, 50 100, 100 100")
 */
fun bezierPath(start: Point, end: Point): String {
    val controlOffset = (end.x - start.x) / 2
    return "M ${start.x} ${start.y} C ${start.x + controlOffset} ${start.y}, ${end.x - controlOffset} ${end.y}, ${end.x} ${end.y}"
}

/**
 * Calculates the right edge center point of an element for connection drawing.
 * Used for producer nodes connecting to types.
 *
 * @param element The DOM element to calculate the point for
 * @param containerRect The bounding rect of the container for relative positioning
 * @return The right-center point of the element relative to the container
 */
fun getRightEdgeCenter(element: Element, containerRect: org.w3c.dom.DOMRect): Point {
    val rect = element.getBoundingClientRect()
    return Point(
        x = rect.right - containerRect.left,
        y = rect.top + (rect.height / 2) - containerRect.top
    )
}

/**
 * Calculates the left edge center point of an element for connection drawing.
 * Used for types connecting to consumer nodes.
 *
 * @param element The DOM element to calculate the point for
 * @param containerRect The bounding rect of the container for relative positioning
 * @return The left-center point of the element relative to the container
 */
fun getLeftEdgeCenter(element: Element, containerRect: org.w3c.dom.DOMRect): Point {
    val rect = element.getBoundingClientRect()
    return Point(
        x = rect.left - containerRect.left,
        y = rect.top + (rect.height / 2) - containerRect.top
    )
}

/**
 * Data class representing the computed positions of all nodes for drawing connections.
 * Positions are computed relative to the container element.
 */
data class NodePositions(
    val containerRect: org.w3c.dom.DOMRect,
    val producerPositions: Map<String, Point>,
    val typeLeftPositions: Map<String, Point>,
    val typeRightPositions: Map<String, Point>,
    val consumerPositions: Map<String, Point>
)

/**
 * Computes the positions of all nodes for drawing connection lines.
 * This function queries the DOM for element positions and calculates connection points.
 *
 * @param containerId The ID of the main container element
 * @param producers List of producer IDs to find positions for
 * @param types List of type IDs to find positions for
 * @param consumers List of consumer IDs to find positions for
 * @return NodePositions with all computed positions, or null if container not found
 */
fun computeNodePositions(
    containerId: String,
    producers: List<String>,
    types: List<String>,
    consumers: List<String>
): NodePositions? {
    val container = document.getElementById(containerId) ?: return null
    val containerRect = container.getBoundingClientRect()

    val producerPositions = producers.mapNotNull { id ->
        val element = document.getElementById("producer-$id")
        element?.let { id to getRightEdgeCenter(it, containerRect) }
    }.toMap()

    val typeLeftPositions = types.mapNotNull { type ->
        val element = document.getElementById("type-$type")
        element?.let { type to getLeftEdgeCenter(it, containerRect) }
    }.toMap()

    val typeRightPositions = types.mapNotNull { type ->
        val element = document.getElementById("type-$type")
        element?.let { type to getRightEdgeCenter(it, containerRect) }
    }.toMap()

    val consumerPositions = consumers.mapNotNull { id ->
        val element = document.getElementById("consumer-$id")
        element?.let { id to getLeftEdgeCenter(it, containerRect) }
    }.toMap()

    return NodePositions(
        containerRect = containerRect,
        producerPositions = producerPositions,
        typeLeftPositions = typeLeftPositions,
        typeRightPositions = typeRightPositions,
        consumerPositions = consumerPositions
    )
}

/**
 * Calculates the clip path rectangle for the visible content area.
 * This clips lines to only show within the column content areas.
 */
fun getClipRect(containerId: String): String? {
    val container = document.getElementById(containerId) ?: return null
    val containerRect = container.getBoundingClientRect()

    // Find the column content areas to determine clip bounds
    val producersContent = document.getElementById("producers-content")
    val typesContent = document.getElementById("types-content")
    val consumersContent = document.getElementById("consumers-content")

    if (producersContent == null || typesContent == null || consumersContent == null) return null

    val producersRect = producersContent.getBoundingClientRect()
    val consumersRect = consumersContent.getBoundingClientRect()

    // Clip from the left of producers to the right of consumers
    // and from the top of content areas to the bottom
    val left = producersRect.left - containerRect.left
    val top = producersRect.top - containerRect.top
    val right = consumersRect.right - containerRect.left
    val bottom = producersRect.bottom - containerRect.top

    return "M $left $top L $right $top L $right $bottom L $left $bottom Z"
}

/** Stroke color for highlighted connections. */
private const val HIGHLIGHTED_STROKE_COLOR = "#6B6B9E"

/**
 * Composable that renders the SVG overlay with connection lines.
 * Lines are drawn between producers and types, and between types and consumers.
 * Uses inline SVG styles for cross-browser compatibility.
 *
 * @param containerId The ID of the container element for position calculations
 * @param producerToTypeConnections List of connections from producers to types
 * @param typeToConsumerConnections List of connections from types to consumers
 * @param positions Pre-computed node positions (if available)
 */
@Composable
fun ConnectionOverlay(
    containerId: String,
    producerToTypeConnections: List<Connection>,
    typeToConsumerConnections: List<Connection>,
    positions: NodePositions?
) {
    if (positions == null) return

    // Get clip rectangle for the content area
    val clipRect = getClipRect(containerId)

    // Build SVG content - only show highlighted connections
    val svgContent = buildString {
        // Add defs for clip path and arrow marker
        append("""<defs>""")

        // Clip path for content area
        if (clipRect != null) {
            append("""<clipPath id="content-clip"><path d="$clipRect"/></clipPath>""")
        }

        // Arrow marker definition
        append("""
            <marker id="arrowhead" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto" markerUnits="strokeWidth">
                <path d="M0,0 L0,6 L6,3 z" fill="$HIGHLIGHTED_STROKE_COLOR" opacity="0.6"/>
            </marker>
        """.trimIndent())

        // Animation style
        append("""
            <style>
                @keyframes flowAnimation {
                    from { stroke-dashoffset: 20; }
                    to { stroke-dashoffset: 0; }
                }
                .flow-line {
                    stroke-dasharray: 5, 5;
                    animation: flowAnimation 1.2s linear infinite;
                }
            </style>
        """.trimIndent())

        append("""</defs>""")

        // Start clipped group
        if (clipRect != null) {
            append("""<g clip-path="url(#content-clip)">""")
        }

        // Producer to type connections (only highlighted)
        producerToTypeConnections.filter { it.highlighted }.forEach { connection ->
            val startPoint = positions.producerPositions[connection.fromId]
            val endPoint = positions.typeLeftPositions[connection.toId]

            if (startPoint != null && endPoint != null) {
                val pathData = bezierPath(startPoint, endPoint)
                append("""<path class="flow-line" d="$pathData" fill="none" stroke="$HIGHLIGHTED_STROKE_COLOR" stroke-width="1.5" opacity="0.6" marker-end="url(#arrowhead)"/>""")
            }
        }

        // Type to consumer connections (only highlighted)
        typeToConsumerConnections.filter { it.highlighted }.forEach { connection ->
            val startPoint = positions.typeRightPositions[connection.fromId]
            val endPoint = positions.consumerPositions[connection.toId]

            if (startPoint != null && endPoint != null) {
                val pathData = bezierPath(startPoint, endPoint)
                append("""<path class="flow-line" d="$pathData" fill="none" stroke="$HIGHLIGHTED_STROKE_COLOR" stroke-width="1.5" opacity="0.6" marker-end="url(#arrowhead)"/>""")
            }
        }

        if (clipRect != null) {
            append("""</g>""")
        }
    }

    // Use key to force re-render when content changes
    key(svgContent) {
        DisposableEffect(svgContent) {
            val container = document.getElementById(containerId)
            if (container != null) {
                // Remove existing SVG overlay if present
                val existingSvg = document.getElementById("connection-lines-svg")
                existingSvg?.remove()

                // Create SVG element
                val svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
                svg.id = "connection-lines-svg"
                svg.setAttribute("style", "position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 10;")
                svg.innerHTML = svgContent
                container.appendChild(svg)
            }

            onDispose {
                document.getElementById("connection-lines-svg")?.remove()
            }
        }
    }
}
